import { Container } from '@/ui/design-system/Container'
import { MEDIA_CONFIG } from '@/shared/utils/media'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { useRef } from 'react'

export function ProblemStatement() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 })
  const reducedMotion = useReducedMotion()
  const inView = reducedMotion || isInView

  const fadeInLeft = {
    hidden: reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }
    }
  }

  const fadeInRight = {
    hidden: reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }
    }
  }

  return (
    <section ref={sectionRef} className="py-20 lg:py-40 bg-earth-50">
      <Container size="app">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Problem Side - Group Chat Chaos */}
          <motion.div
            className="relative"
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={fadeInLeft}
          >
            <div className="relative overflow-hidden rounded-2xl border border-earth-200 shadow-xl dark:border-earth-700">
              <img
                src={MEDIA_CONFIG.problem.groupChatGif}
                alt="Chaotic group chat showing how hard it is to coordinate trips via messaging"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Solution Side */}
          <motion.div
            className="flex flex-col gap-6"
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={fadeInRight}
          >
            <div className="flex items-center">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                Tired of Group Chat Chaos?
              </h2>
            </div>

            <p className="text-lg text-muted-foreground leading-relaxed">
              Planning trips in group chats is overwhelming. Messages get lost, important details are buried, and coordination becomes a nightmare. There has to be a better way.
            </p>

            <ul className="space-y-3">
              {[
                'Messages scattered across multiple threads',
                'Critical information gets buried',
                'No single source of truth for trip details',
                'Hard to stay organized and informed'
              ].map((item, idx) => (
                <motion.li
                  key={idx}
                  className="flex items-start gap-3"
                  initial={reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ duration: 0.4, delay: 0.3 + (idx * 0.1), ease: [0.6, -0.05, 0.01, 0.99] as const }}
                >
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-error flex-shrink-0" />
                  <span className="text-base text-earth-700">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </Container>
    </section>
  )
}
